import { forwardRef } from '@heroui/system';
import { MdOutlineCancel, MdOutlineKeyboardArrowDown } from 'react-icons/md';
import { Autocomplete, AutocompleteProps } from '@heroui/react';

export interface CustomAutocompleteProps extends AutocompleteProps {
  hideSelectorButton?: boolean;
}

const CustomAutocomplete = forwardRef<'input', CustomAutocompleteProps>((props, ref) => {
  const { label, children, hideSelectorButton, ...restProps } = props;

  return (
    <Autocomplete
      ref={ref}
      label={label}
      isVirtualized={false}
      selectorIcon={
        hideSelectorButton ? null : (
          <span>
            <MdOutlineKeyboardArrowDown size={24} className="tmdx-select__icon-arrow" />
          </span>
        )
      }
      clearIcon={<MdOutlineCancel size={20} />}
      classNames={{
        base: 'tmdx-input tmdx-autocomplete group',
        selectorButton: !!hideSelectorButton && 'pointer-events-none',
        listbox: 'p-0',
        listboxWrapper: 'p-0',
        popoverContent: 'bg-base-60 shadow-xl rounded-lg p-0',
        clearButton: 'bg-transparent group-data-[invalid=true]:bg-transparent min-w-5 w-5',
      }}
      inputProps={{
        classNames: {
          inputWrapper: [
            'bg-base-60',
            'group-data-[focus=true]:bg-base-40',
            'group-data-[invalid=true]:bg-base-60!',
            'group-data-[invalid=true]:group-data-[hover=true]:bg-base-60!',
            'tmdx-input__wrapper',
            'rounded-lg',
            'transition-none!',
            !label && 'min-h-8 h-full',
            !!label && 'hasLabel',
            'shadow-none',
          ],
          input: [
            'pe-0!',
            'overflow-hidden',
            'text-ellipsis',
            'text-base-5',
            'placeholder:text-base-30',
            'text-m-r!',
            'group-data-[invalid=true]:text-base-5!',
            'tmdx-input__item',
          ],
          label: [
            'text-base-20',
            'text-s-r',
            'tmdx-input__label',
            'group-focus-within:text-base-10',
            'group-data-[invalid=true]:text-danger!',
          ],
        },
      }}
      listboxProps={{
        itemClasses: {
          base: 'px-3 py-2 *:text-m-r! rounded-lg *:text-base-5 data-[hover=true]:bg-base-50! data-[selected=true]:bg-base-50! [&[data-selected=true]_[data-hover=true]]:bg-base-50! [&[data-selected=true]_[aria-hidden=true]]:hidden',
        },
      }}
      popoverProps={{
        classNames: {
          base: 'p-0',
        },
      }}
      selectorButtonProps={{
        disableRipple: true,
        className: 'data-[hover=true]:bg-transparent',
      }}
      {...restProps}
    >
      {children}
    </Autocomplete>
  );
});

CustomAutocomplete.displayName = 'CustomAutocomplete';

export default CustomAutocomplete;
